import { createContext, useContext } from "react";

/**
 * Modal ↔ Modal.Header.Title 사이의 accessible-name 배선 채널 (internal — `index.ts` 비노출).
 *
 * RFC-modal-primitive에 따라 header는 prop이 아니라 composition이다. `role="dialog"`는 accessible name이
 * 필수이므로, Modal.Header.Title이 자기 타이틀 `<h2>` id를 이 registry로 등록하면 Modal이 그 id를
 * `aria-labelledby`로 연결한다. (provider 밖이면 `useModalLabelRegistry()`가 `null` → 등록 없이
 * 동작한다.)
 *
 * Owns:
 * - Modal.Header.Title이 타이틀 `<h2>` id를 Modal에 등록/해제하는 경로 계약.
 *
 * Does not own:
 * - id 생성(Modal.Header.Title이 `useId`로 생성)·aria 속성 적용(Modal이 수행)·타이틀 렌더.
 */
export interface ModalLabelRegistry {
  /** Modal.Header.Title이 타이틀 `<h2>` id를 등록 — Modal이 `aria-labelledby`로 연결한다. */
  register: (titleId: string) => void;
  /** 타이틀 언마운트(또는 title 제거) 시 해제. 현재 등록된 id와 일치할 때만 비운다. */
  unregister: (titleId: string) => void;
}

// provider 밖에서는 null — 그땐 자동 배선 없이 동작한다.
export const ModalLabelContext = createContext<ModalLabelRegistry | null>(null);

export const useModalLabelRegistry = (): ModalLabelRegistry | null => useContext(ModalLabelContext);

/**
 * Modal → Modal.Header.Close 사이의 닫기 배선 채널 (internal — `index.ts` 비노출).
 *
 * Modal이 자기 `onClose`를 여기 실어주면 `Modal.Header.Close`가 prop 주입 없이 그걸 호출한다 —
 * Escape/backdrop 클릭과 동일하게 dismiss가 `onClose` 하나로 통일된다(가드/confirm 로직을 한 곳에).
 * provider 밖이면 null → Close는 no-op.
 */
export const ModalCloseContext = createContext<(() => void) | null>(null);

export const useModalClose = (): (() => void) | null => useContext(ModalCloseContext);
